<!--eslint-disable vue/multi-word-component-names-->
<template>
  <div id="category">
    <CategoryTree
      :category-id="id"
      :search-specs-str="searchSpecsStr"
    />

    <template v-if="$fetchState.pending">
      <content-placeholders class="placeholder-grid">
        <div class="first">
          <content-placeholders-img />
        </div>
        <div class="second">
          <content-placeholders-img />
        </div>
        <div class="third">
          <content-placeholders-img />
        </div>
      </content-placeholders>
    </template>
    <template v-else>
      <ProductList
        :category-id="id"
        :search-specs-str="searchSpecsStr"
        :should-load="isLoadingMoreProducts"
        :load-direction="loadDirection"
        :products="products"
        :items-per-page="itemsPerPage"
        :page="page"
        :loaded-page="loadedPage"
        @loadMore="searchMoreProduct(1)"
        @loadMoreUp="searchMoreProduct(-1)"
        @pageUp="pageUpDown"
        @pageDown="pageUpDown"
      />
      <CategoryDescription :category-id="id" />
    </template>
    <StructuredBreadcrumbData />
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  useFetch,
  useRoute,
  computed,
} from '@nuxtjs/composition-api';
import { facetGetters, useFacet } from '@vue-storefront/gemini';
import { CacheTagPrefix, useCache } from '@gemini/vsf-cache';
import { useUiHelpers } from '~/composables';
import cacheControl from '~/helpers/cacheControl';
import useGeminiApi from '~/composables/useGeminiApi/useGeminiApi';
import { useGetAlternateMatching } from '~/helpers/alternate/getAlternateMatching';
import CategoryDescription from '~/components/Category/CategoryDescription.vue';
import StructuredBreadcrumbData from '~/components/StructuredData/StructuredBreadcrumbData.vue';
import CategoryTree from '~/components/Category/CategoryTree';
import ProductList from '~/components/Category/ProductList';

// TODO(addToCart qty, horizontal): https://github.com/vuestorefront/storefront-ui/issues/1606
export default defineComponent({
  name: 'CategoryPage',
  components: {
    StructuredBreadcrumbData,
    CategoryDescription,
    CategoryTree,
    ProductList,
  },
  middleware: cacheControl({
    'max-age': 60,
    'stale-when-revalidate': 5,
  }),
  transition: 'fade',
  props: {
    categoryId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const route = useRoute();
    const page = ref(
      !Number.isNaN(route?.value?.query?.page) && route?.value?.query?.page >= 1
        ? Number(route.value.query.page)
        : 1,
    );
    const loadedPage = ref(page.value);
    const id = props?.categoryId || route?.value?.params?.categoryId || '';
    const products = ref([]);
    const isLoadingMoreProducts = ref(false);
    const loadDirection = ref(1);
    const seoAlternateurls = ref([]);
    const alternate = computed(() => seoAlternateurls.value);
    const { getGeminiData, geminiData } = useGeminiApi();
    const { getAlternateArray } = useGetAlternateMatching();
    const uiHelpers = useUiHelpers();
    const filtersStr = JSON.stringify(uiHelpers.getFacetsFromURL());
    const searchSpecsStr = `facetId:${id}:${filtersStr}`;
    const { result, search } = useFacet(searchSpecsStr);
    const { addTags } = useCache();

    const getGeminiDataFunction = async () => {
      await getGeminiData(`grn:product-list:product-list::${id}`);
      seoAlternateurls.value = getAlternateArray(
        geminiData.value,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return JSON.parse(JSON.stringify(seoAlternateurls.value));
    };

    const itemsPerPage = 14;
    const categoryUid = `grn:product-list:product-list::${id}`;
    useFetch(async () => {
      await getGeminiDataFunction();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      await search({
        ...uiHelpers.getFacetsFromURL(),
        categoryId: categoryUid,
        itemsPerPage,
      });
      if (facetGetters.getProducts(result.value) && facetGetters.getProducts(result.value).length > 0) {
        products.value = facetGetters.getProducts(result.value);
        addTags([
          { prefix: CacheTagPrefix.View, value: 'category' },
          { prefix: CacheTagPrefix.Category, value: id },
        ]);
      }
    });

    const searchMoreProduct = async (direction) => {
      // is last page or is before first page
      if (
        isLoadingMoreProducts.value
        || (result?.value?.data?.isLastPage && direction === 1)
        || (page.value <= 1 && direction === -1)
      ) {
        return;
      }

      isLoadingMoreProducts.value = true;
      loadDirection.value = direction;

      const pageValue = Number.parseInt(page.value, 10);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const directionValue = Number.parseInt(direction, 10);
      page.value = pageValue + directionValue;

      loadedPage.value = page.value;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      await search({
        ...uiHelpers.getFacetsFromURL(),
        categoryId: categoryUid,
        page: page.value,
      });

      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('page', page.value.toString());

      // eslint-disable-next-line no-restricted-globals
      history.pushState(
        {},
        null,
        `${route.value.path}?${queryParams.toString()}`,
      );

      products.value = direction === -1
        ? [...facetGetters.getProducts(result.value), ...products.value]
        : [...products.value, ...facetGetters.getProducts(result.value)];

      isLoadingMoreProducts.value = false;
    };

    const pageUpDown = (pageAndDirection) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      page.value = Number.parseInt(pageAndDirection.page, 10);

      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('page', page.value.toString());

      // eslint-disable-next-line no-restricted-globals
      history.pushState(
        {},
        null,
        `${route.value.path}?${queryParams.toString()}`,
      );
    };

    return {
      isLoadingMoreProducts,
      loadDirection,
      searchMoreProduct,
      searchSpecsStr,
      id,
      products,
      result,
      itemsPerPage,
      pageUpDown,
      page,
      loadedPage,
      alternate,
    };
  },
  head() {
    let lcpImage;
    let link = [];
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      lcpImage = facetGetters.getProducts(this.result);
    } catch {
      console.error('Could not find main image src to preload.');
    }
    link = lcpImage?.[2]?.thumbnail?.url ? [
      {
        rel: 'preload',
        as: 'image',
        href: `${lcpImage[2].thumbnail.url}?f=a&w=700`,
      },
      ...this.alternate,
    ] : [
      ...this.alternate,
    ];
    return {
      link,
    };
  },
});
</script>

<style lang="scss" scoped>
.placeholder-grid {
  max-width: 1246px;
  height: 700px;
  margin: 40px auto 0;
  display: grid;
  grid-template-columns: 0.5fr 0.5fr 1fr;
  grid-template-rows: 1fr;
  gap: 0 24px;
  grid-template-areas: 'first second third';
  @include for-mobile {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    height: auto;
    gap: 10px;
    padding: 0 24px;
    margin: 0;
    grid-template-areas:
      'first second'
      'third third';
    .first,
    .second,
    .third {
      height: 300px;
      margin: 0;
    }
  }

  .first {
    grid-area: first;
  }

  .second {
    grid-area: second;
  }

  .third {
    grid-area: third;
  }

  .vue-content-placeholders-img {
    height: 100%;
  }
}

#category {
  box-sizing: border-box;
  background: #f6f3ef;
  @include for-desktop {
    max-width: 100%;
    margin: 0 auto;
  }

  .products__pagination {
    @include for-desktop {
      margin: 0;
      justify-content: center;
    }
  }
}

.not-product {
  background: #f6f3ef;
  margin-top: 30px;

  .container {
    margin: 0 auto;
    max-width: 1246px;
    padding: 85px 0;

    h1 {
      text-align: center;
    }
  }
}

@media screen and (min-width: 1024px) and (max-width: 1250px) {
  #category {
    padding: 0 31px;
  }
}

.main {
  &.section {
    padding: var(--spacer-xs);
    @include for-desktop {
      padding: 0;
    }
  }
}

.breadcrumbs {
  margin: var(--spacer-base) auto var(--spacer-lg);
}

.navbar {
  position: relative;
  display: flex;
  border: 1px solid var(--c-light);
  border-width: 0 0 1px 0;
  @include for-desktop {
    border-width: 1px 0 1px 0;
  }

  &.section {
    padding: var(--spacer-sm);
    @include for-desktop {
      padding: 0;
    }
  }

  &__aside,
  &__main {
    display: flex;
    align-items: center;
    padding: var(--spacer-sm) 0;
  }

  &__aside {
    flex: 0 0 15%;
    padding: var(--spacer-sm) var(--spacer-sm);
    border: 1px solid var(--c-light);
    border-width: 0 1px 0 0;
  }

  &__main {
    flex: 1;
    padding: 0;
    justify-content: space-between;
    @include for-desktop {
      padding: var(--spacer-xs) var(--spacer-xl);
    }
  }

  &__title {
    --heading-title-font-weight: var(--font-weight--semibold);
    --heading-title-font-size: var(--font-size--xl);
  }

  &__filters-icon {
    margin: 0 0 0 var(--spacer-xs);
    order: 1;
    @include for-desktop {
      margin: 0 var(--spacer-xs) 0 0;
      order: 0;
    }
  }

  &__filters-button {
    display: flex;
    align-items: center;
    --button-font-size: var(--font-size--base);
    --button-text-decoration: none;
    --button-color: var(--c-link);
    --button-font-weight: var(--font-weight--normal);
    @include for-mobile {
      --button-font-weight: var(--font-weight--medium);
      order: 2;
    }

    svg {
      fill: var(--c-text-muted);
      transition: fill 150ms ease;
    }

    &:hover {
      svg {
        fill: var(--c-primary);
      }
    }
  }

  &__label {
    font-family: var(--font-family--secondary);
    font-weight: var(--font-weight--normal);
    color: var(--c-text-muted);
    @include for-desktop {
      color: var(--c-link);
      margin: 0 var(--spacer-2xs) 0 0;
    }
  }

  &__select {
    --select-width: 220px;
    --select-padding: 0;
    --select-height: auto;
    --select-selected-padding: 0 var(--spacer-lg) 0 var(--spacer-2xs);
    --select-margin: 0;
    --select-option-font-size: var(--font-size-sm);
    --select-error-message-height: 0;

    ::v-deep .sf-select__dropdown {
      font-size: var(--font-size-sm);
      font-family: var(--font-family--secondary);
      font-weight: var(--font-weight--light);
      margin: 0;
    }

    ::v-deep .sf-select__placeholder {
      --select-option-font-size: var(--font-size-sm);
    }
  }

  &__sort {
    display: flex;
    align-items: center;
    margin: 0 auto 0 var(--spacer-2xl);
  }

  &__counter {
    font-family: var(--font-family--secondary);
    order: 1;
    @include for-desktop {
      margin: auto 0 auto auto;
      order: 0;
    }
  }

  &__view {
    display: flex;
    align-items: center;
    order: 0;
    @include for-desktop {
      margin: 0 0 0 var(--spacer-2xl);
      order: 0;
    }

    &-icon {
      cursor: pointer;
      margin: 0 var(--spacer-base) 0 0;

      &:last-child {
        margin: 0;
      }
    }

    &-label {
      margin: 0 var(--spacer-sm) 0 0;
      font: var(--font-weight--normal) var(--font-size--base) / 1.6
        var(--font-family--secondary);
      text-decoration: none;
      color: var(--c-link);
    }
  }
}

.sort-by {
  flex: unset;
  width: 11.875rem;
}

.main {
  display: flex;
}

.sidebar {
  flex: 0 0 15%;
  padding: var(--spacer-sm);
  border: 1px solid var(--c-light);
  border-width: 0 1px 0 0;
}

.sidebar-filters {
  --overlay-z-index: 3;
  --sidebar-title-display: none;
  --sidebar-top-padding: 0;
  @include for-desktop {
    --sidebar-content-padding: 0 var(--spacer-xl);
    --sidebar-bottom-padding: 0 var(--spacer-xl);
  }
}

.products {
  box-sizing: border-box;
  flex: 1;
  margin: 0;

  &__grid {
    justify-content: center;
    @include for-desktop {
      justify-content: flex-start;
    }
  }

  &__grid,
  &__list {
    display: flex;
    flex-wrap: wrap;
  }

  &__product-card {
    --product-card-title-margin: var(--spacer-base) 0 0 0;
    --product-card-title-font-weight: var(--font-weight--medium);
    --product-card-title-margin: var(--spacer-xs) 0 0 0;
    flex: 1 1 50%;
    @include for-desktop {
      --product-card-title-font-weight: var(--font-weight--normal);
      --product-card-add-button-bottom: var(--spacer-base);
      --product-card-title-margin: var(--spacer-sm) 0 0 0;
    }
  }

  &__product-card-horizontal {
    flex: 0 0 100%;
    @include for-mobile {
      ::v-deep .sf-image {
        --image-width: 5.3125rem;
        --image-height: 7.0625rem;
      }
    }

    &__add-to-wishlist {
      @include for-mobile {
        margin: 1rem auto;
      }
      display: block;
    }
  }

  &__slide-enter {
    opacity: 0;
    transform: scale(0.5);
  }

  &__slide-enter-active {
    transition: all 0.2s ease;
    transition-delay: calc(0.1s * var(--index));
  }

  @include for-desktop {
    &__grid {
      margin: var(--spacer-sm) 0 0 var(--spacer-sm);
    }
    &__pagination {
      display: flex;
      justify-content: flex-start;
      margin: var(--spacer-xl) 0 0 0;
    }
    &__product-card-horizontal {
      margin: var(--spacer-lg) 0;
    }
    &__product-card {
      flex: 1 1 25%;
    }
    &__list {
      margin: 0 0 0 var(--spacer-sm);
    }
  }

  &__show-on-page {
    display: flex;
    justify-content: flex-end;
    align-items: baseline;

    &__label {
      font-family: var(--font-family--secondary);
      font-size: var(--font-size--sm);
    }
  }
}

.loading {
  margin: var(--spacer-3xl) auto;
  @include for-desktop {
    margin-top: 6.25rem;
  }

  &--categories {
    @include for-desktop {
      margin-top: 3.75rem;
    }
  }
}

::v-deep .sf-sidebar__aside {
  --sidebar-z-index: 3;
}

.filters {
  &__title {
    --heading-title-font-size: var(--font-size--xl);
    margin: var(--spacer-xl) 0 var(--spacer-base) 0;

    &:first-child {
      margin: calc(var(--spacer-xl) + var(--spacer-base)) 0 var(--spacer-xs) 0;
    }
  }

  &__colors {
    display: flex;
  }

  &__color {
    margin: var(--spacer-xs) var(--spacer-xs) var(--spacer-xs) 0;
  }

  &__chosen {
    color: var(--c-text-muted);
    font-weight: var(--font-weight--normal);
    font-family: var(--font-family--secondary);
    position: absolute;
    right: var(--spacer-xl);
  }

  &__item {
    --radio-container-padding: 0 var(--spacer-sm) 0 var(--spacer-xl);
    --radio-background: transparent;
    --filter-label-color: var(--c-secondary-variant);
    --filter-count-color: var(--c-secondary-variant);
    --checkbox-padding: 0 var(--spacer-sm) 0 var(--spacer-xl);
    padding: var(--spacer-sm) 0;
    border-bottom: 1px solid var(--c-light);

    &:last-child {
      border-bottom: 0;
    }

    @include for-desktop {
      --checkbox-padding: 0;
      margin: var(--spacer-sm) 0;
      border: 0;
      padding: 0;
    }
  }

  &__accordion-item {
    --accordion-item-content-padding: 0;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    width: 100vw;
  }

  &__buttons {
    margin: var(--spacer-sm) 0;
  }

  &__button-clear {
    --button-background: var(--c-light);
    --button-color: var(--c-dark-variant);
    margin: var(--spacer-xs) 0 0 0;
  }
}
</style>
